import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SiteOffice } from '@/services/siteOfficeService';

interface SiteOfficeDataStatus {
  [siteOfficeName: string]: boolean;
}

export const useSiteOfficeDataCheck = (siteOffices: SiteOffice[]) => {
  const [dataStatus, setDataStatus] = useState<SiteOfficeDataStatus>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkDataAvailability = async () => {
      if (!siteOffices || siteOffices.length === 0) {
        console.log('🔍 No site offices provided, setting loading to false');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🔍 Checking data availability for', siteOffices.length, 'site offices');
        
        const status: SiteOfficeDataStatus = {};

        // Check each site office for data availability
        for (const office of siteOffices) {
          try {
            console.log(`🔍 Checking data for site office: ${office.name} (ID: ${office.id})`);

            // Check for projects in this site office
            const { data: projectsData, error: projectsError } = await supabase
              .from('projects')
              .select('id, title')
              .eq('site_office_id', office.id);

            if (projectsError) {
              console.error(`❌ Error checking projects for ${office.name}:`, projectsError);
              status[office.name] = false;
              continue;
            }

            // Check for materials and labor data using project_id instead of site_office_id
            // since those tables don't have site_office_id column
            const projectIds = projectsData?.map(p => p.id) || [];
            
            let hasMaterials = false;
            let hasLabor = false;
            
            if (projectIds.length > 0) {
              const [materialsResult, laborResult] = await Promise.all([
                supabase
                  .from('materials')
                  .select('id')
                  .in('project_id', projectIds)
                  .limit(5),
                supabase
                  .from('labors')
                  .select('id')
                  .in('project_id', projectIds)
                  .limit(5)
              ]);
              
              hasMaterials = (materialsResult.data && materialsResult.data.length > 0) || false;
              hasLabor = (laborResult.data && laborResult.data.length > 0) || false;
            }

            // Site office has data if it has projects OR materials OR labor
            const hasProjects = projectsData && projectsData.length > 0;

            status[office.name] = hasProjects || hasMaterials || hasLabor;

            console.log(`📊 Data check for ${office.name}:`, {
              siteOfficeId: office.id,
              projectsCount: projectsData?.length || 0,
              hasProjects,
              hasMaterialsOrLabor: hasMaterials || hasLabor,
              finalStatus: status[office.name]
            });

            // Log some project details if found
            if (hasProjects) {
              console.log(`📋 Projects found for ${office.name}:`, 
                projectsData.map(p => ({ id: p.id, title: p.title }))
              );
            }

          } catch (error) {
            console.error(`❌ Error checking data for ${office.name}:`, error);
            status[office.name] = false;
          }
        }

        console.log('📊 Final data status:', status);
        setDataStatus(status);
        
        // Summary log
        const totalOffices = siteOffices.length;
        const officesWithData = Object.values(status).filter(Boolean).length;
        console.log(`📊 SUMMARY: ${officesWithData}/${totalOffices} site offices have data`);
        
      } catch (error) {
        console.error('❌ Error checking site office data availability:', error);
      } finally {
        setLoading(false);
      }
    };

    checkDataAvailability();
  }, [siteOffices]);

  const hasData = (officeName: string): boolean => {
    const result = dataStatus[officeName] || false;
    console.log(`🔍 hasData check for "${officeName}":`, result);
    return result;
  };

  return {
    hasData,
    dataStatus,
    loading
  };
}; 